/**
 * ExportTab — Export data in multiple formats.
 *
 * Supports:
 *  - CSV (activities, daily summary, vehicles, infringements)
 *  - PDF driver report (via backend endpoint)
 *  - XLSX compliance workbook (via backend endpoint)
 */

import { useState } from 'react'
import { DriverAnalysis } from '../types'

const API_BASE = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api`
  : '/api'

interface Props {
  analysis: DriverAnalysis
  driverId?: number
}

type ExportFormat = 'csv_activities' | 'csv_daily' | 'csv_vehicles' | 'csv_infringements' | 'xlsx' | 'pdf'

export function ExportTab({ analysis, driverId }: Props) {
  const [exporting, setExporting] = useState<ExportFormat | null>(null)
  const [error, setError] = useState<string | null>(null)

  const driver = analysis.driver
  const driverName = `${driver?.first_names ?? 'Driver'}_${driver?.surname ?? 'Unknown'}`.replace(/\s+/g, '_')

  async function handleExport(format: ExportFormat) {
    setExporting(format)
    setError(null)
    try {
      const token = localStorage.getItem('tourx_token')

      if (driverId && token) {
        let serverFormat = format as string
        if (format.startsWith('csv_')) {
          const csvType = format.replace('csv_', '')
          serverFormat = `csv/${csvType}`
        }
        const url = `${API_BASE}/cards/export/${serverFormat}/${driverId}?token=${encodeURIComponent(token)}`
        window.open(url, '_blank')
        setExporting(null)
        return
      }

      if (format.startsWith('csv_')) {
        exportCSV(format, analysis, driverName)
      } else if (format === 'xlsx' || format === 'pdf') {
        await exportFromBackend(format, analysis, driverName, driverId)
      }
    } catch (err: any) {
      console.error('Export failed:', err)
      setError(err.message || 'Export failed')
    } finally {
      setExporting(null)
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Export Data</h3>
        <p className="text-sm text-gray-400">
          Download tachograph data for compliance, payroll, or fleet management.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* CSV Exports */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">CSV Exports</h3>
        <div className="border border-gray-200 rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500">Export</th>
                <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500">Contents</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500">Records</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500 w-28"></th>
              </tr>
            </thead>
            <tbody>
              <ExportRow
                title="Activity Log"
                description="Every activity change record: date, type, start, end, duration, vehicle"
                rows={analysis.activities_count ?? 0}
                loading={exporting === 'csv_activities'}
                onClick={() => handleExport('csv_activities')}
              />
              <ExportRow
                title="Daily Summary"
                description="One row per day: driving hours, work, rest, distance, status, vehicle"
                rows={analysis.daily_breakdown?.length ?? 0}
                loading={exporting === 'csv_daily'}
                onClick={() => handleExport('csv_daily')}
              />
              <ExportRow
                title="Vehicles Used"
                description="Vehicle records: registration, VIN, dates, odometer readings"
                rows={analysis.vehicles_used?.length ?? 0}
                loading={exporting === 'csv_vehicles'}
                onClick={() => handleExport('csv_vehicles')}
              />
              <ExportRow
                title="Infringements"
                description="All compliance violations: type, severity, date, regulation"
                rows={analysis.infringements?.length ?? 0}
                loading={exporting === 'csv_infringements'}
                onClick={() => handleExport('csv_infringements')}
              />
            </tbody>
          </table>
        </div>
      </div>

      {/* Report Exports */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Reports</h3>
        <div className="border border-gray-200 rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500">Export</th>
                <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500">Contents</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500 w-28"></th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-2 px-3 font-medium text-gray-900">XLSX Compliance Workbook</td>
                <td className="py-2 px-3 text-xs text-gray-500">Multi-sheet Excel workbook with daily breakdown, weekly totals, infringements, and vehicle records. DVSA audit-ready.</td>
                <td className="py-2 px-3 text-right">
                  <button
                    onClick={() => handleExport('xlsx')}
                    disabled={exporting === 'xlsx'}
                    className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-wait"
                  >
                    {exporting === 'xlsx' ? 'Generating...' : 'Download'}
                  </button>
                </td>
              </tr>
              <tr className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-2 px-3 font-medium text-gray-900">PDF Driver Report</td>
                <td className="py-2 px-3 text-xs text-gray-500">Printable driver report with summary, daily charts, infringement list, and vehicle history. A4 format.</td>
                <td className="py-2 px-3 text-right">
                  <button
                    onClick={() => handleExport('pdf')}
                    disabled={exporting === 'pdf'}
                    className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-wait"
                  >
                    {exporting === 'pdf' ? 'Generating...' : 'Download'}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Info */}
      <div className="border-t border-gray-200 pt-3">
        <p className="text-[11px] text-gray-400">
          CSV files open directly in Excel or Google Sheets. The XLSX workbook includes formatting, formulas, and compliance status highlighting.
          The PDF report is designed for printing and DVSA inspections. All exports include the driver name and date range in the filename.
        </p>
      </div>
    </div>
  )
}

/* ── Table row component ─────────────────────────────────────── */

function ExportRow({ title, description, rows, loading, onClick }: {
  title: string
  description: string
  rows: number
  loading: boolean
  onClick: () => void
}) {
  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50">
      <td className="py-2 px-3 font-medium text-gray-900">{title}</td>
      <td className="py-2 px-3 text-xs text-gray-500">{description}</td>
      <td className="py-2 px-3 text-right text-gray-400 text-xs">{rows > 0 ? rows : '—'}</td>
      <td className="py-2 px-3 text-right">
        <button
          onClick={onClick}
          disabled={loading}
          className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-wait"
        >
          {loading ? 'Generating...' : 'Download'}
        </button>
      </td>
    </tr>
  )
}

/* ── CSV generation (client-side) ────────────────────────────── */

function escapeCsvField(value: any): string {
  if (value == null) return ''
  const str = String(value)
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

function exportCSV(format: ExportFormat, analysis: DriverAnalysis, driverName: string) {
  let csv = ''
  let filename = ''

  if (format === 'csv_activities') {
    const headers = ['Date', 'Activity', 'Start', 'End', 'Duration (min)', 'Card Status', 'Slot', 'Vehicle', 'Distance (km)']
    csv = headers.join(',') + '\n'
    for (const day of analysis.daily_breakdown ?? []) {
      const vehs = day.vehicles?.map((v: any) => v.reg).join('; ') ?? ''
      csv += [
        day.date, 'summary', '', '',
        day.driving_minutes + day.work_minutes + day.rest_minutes + day.availability_minutes,
        '', '', escapeCsvField(vehs), day.distance_km,
      ].join(',') + '\n'
      if (day.driving_minutes > 0)
        csv += [day.date, 'driving', '', '', day.driving_minutes, '', '', escapeCsvField(vehs), ''].join(',') + '\n'
      if (day.work_minutes > 0)
        csv += [day.date, 'work', '', '', day.work_minutes, '', '', escapeCsvField(vehs), ''].join(',') + '\n'
      if (day.rest_minutes > 0)
        csv += [day.date, 'rest', '', '', day.rest_minutes, '', '', escapeCsvField(vehs), ''].join(',') + '\n'
      if (day.availability_minutes > 0)
        csv += [day.date, 'availability', '', '', day.availability_minutes, '', '', escapeCsvField(vehs), ''].join(',') + '\n'
    }
    filename = `${driverName}_activities.csv`

  } else if (format === 'csv_daily') {
    const headers = ['Date', 'Day', 'Driving (h)', 'Driving (min)', 'Work (min)', 'Rest (min)', 'Availability (min)', 'Distance (km)', 'Max Continuous (min)', 'Status', 'Vehicles']
    csv = headers.join(',') + '\n'
    for (const day of analysis.daily_breakdown ?? []) {
      const dow = new Date(day.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short' })
      csv += [
        day.date, dow, day.driving_hours, day.driving_minutes,
        day.work_minutes, day.rest_minutes, day.availability_minutes,
        day.distance_km, day.max_continuous_driving_minutes, day.status,
        escapeCsvField(day.vehicles?.map((v: any) => v.reg).join('; ') ?? ''),
      ].join(',') + '\n'
    }
    filename = `${driverName}_daily_summary.csv`

  } else if (format === 'csv_vehicles') {
    const headers = ['Registration', 'Nation', 'VIN', 'First Use', 'Last Use', 'Odo Start (km)', 'Odo End (km)', 'Distance (km)']
    csv = headers.join(',') + '\n'
    for (const v of analysis.vehicles_used ?? []) {
      const odoStart = typeof v.odometer_start === 'number' ? v.odometer_start : 0
      const odoEnd = typeof v.odometer_end === 'number' ? v.odometer_end : 0
      csv += [
        escapeCsvField(v.registration_number), v.registration_nation ?? '',
        v.vin ?? '', v.first_use ?? '', v.last_use ?? '',
        odoStart || '', odoEnd || '',
        (odoEnd && odoStart) ? odoEnd - odoStart : '',
      ].join(',') + '\n'
    }
    filename = `${driverName}_vehicles.csv`

  } else if (format === 'csv_infringements') {
    const headers = ['Date', 'Type', 'Severity', 'Description', 'Value (min)', 'Limit (min)', 'Regulation']
    csv = headers.join(',') + '\n'
    for (const inf of analysis.infringements ?? []) {
      csv += [
        inf.date, inf.type, inf.severity,
        escapeCsvField(inf.description),
        inf.value_minutes, inf.limit_minutes, inf.regulation,
      ].join(',') + '\n'
    }
    filename = `${driverName}_infringements.csv`
  }

  if (csv) {
    const BOM = '\uFEFF'
    triggerDownload(new Blob([BOM + csv], { type: 'text/csv;charset=utf-8' }), filename)
  }
}

async function exportFromBackend(
  format: 'xlsx' | 'pdf',
  analysis: DriverAnalysis,
  driverName: string,
  driverId?: number,
) {
  const token = localStorage.getItem('tourx_token')

  if (driverId && token) {
    const url = `${API_BASE}/cards/export/${format}/${driverId}?token=${encodeURIComponent(token)}`
    window.open(url, '_blank')
    return
  }

  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const response = await fetch(`${API_BASE}/cards/export/${format}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(analysis),
  })

  if (!response.ok) {
    let detail = response.statusText
    try {
      const body = await response.json()
      detail = body.detail || detail
    } catch { /* ignore */ }
    throw new Error(`Server error: ${detail}`)
  }

  const blob = await response.blob()
  if (blob.size === 0) {
    throw new Error('Server returned empty file — reportlab or openpyxl may not be installed')
  }

  const ext = format === 'xlsx' ? 'xlsx' : 'pdf'
  triggerDownload(blob, `${driverName}_report.${ext}`)
}

function triggerDownload(blob: Blob, filename: string) {
  try {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.style.display = 'none'
    document.body.appendChild(a)
    const evt = new MouseEvent('click', { bubbles: true, cancelable: true, view: window })
    a.dispatchEvent(evt)
    setTimeout(() => {
      try { document.body.removeChild(a) } catch { /* already removed */ }
      URL.revokeObjectURL(url)
    }, 3000)
  } catch (e) {
    console.warn('Blob download failed, trying data URL fallback:', e)
    const reader = new FileReader()
    reader.onload = () => {
      const a = document.createElement('a')
      a.href = reader.result as string
      a.download = filename
      document.body.appendChild(a)
      a.click()
      setTimeout(() => {
        try { document.body.removeChild(a) } catch {}
      }, 1000)
    }
    reader.readAsDataURL(blob)
  }
}
